import { useEffect, useState } from 'react';
import { taskTrackerValuesSchema, type TaskTrackerValues } from '@forecastify/shared';
import { useSession } from '../../session';
import type { TaskFilters } from './tasks-screen';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ J-1 · the tracker's three filters, shared by `/tasks` and a project's tab. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Both routes build their own task query (`tasks-route.tsx`, `delivery-route.tsx`), so the three
 * parameters and the values they offer are written once here rather than a third time.
 */

export const NO_TRACKER_VALUES: TaskTrackerValues = { components: [], fixVersions: [], epics: [] };

/**
 * What the synced tasks in reach carry — `GET /tasks/tracker-values`, narrowed to a project when
 * there is one.
 *
 * ⚠️ **Re-read whenever `after` changes**, and the callers pass their task list: a sync that brings
 * a new fix version arrives as a live event, the route re-reads its tasks, and the menu follows.
 * ⚠️ **Not fatal**, like the metafield definitions: without it the three filters are simply absent,
 * which is also what a workspace with no tracker sees.
 */
export function useTrackerValues(projectId: string, after: unknown): TaskTrackerValues {
  return useTrackerValuesFor(projectId, after).values;
}

/**
 * 3 Nov 2026 · the same, saying WHICH project selection the values answer — `null` until the
 * first answer. An adaptive filter prunes a chosen component only once `loadedFor` is the current
 * selection (`use-filter-options.ts` argues why loading must never prune). `projectId` may be a
 * comma list: the server answers their union.
 */
export function useTrackerValuesFor(
  projectId: string,
  after: unknown,
): { readonly values: TaskTrackerValues; readonly loadedFor: string | null } {
  const session = useSession();
  const [state, setState] = useState<{ values: TaskTrackerValues; loadedFor: string | null }>({
    values: NO_TRACKER_VALUES,
    loadedFor: null,
  });
  useEffect(() => {
    /* An object, so a late answer for an old project is dropped rather than drawn. */
    const alive = { current: true };
    void (async () => {
      if (session.workspaceId === null) return;
      const query = projectId === '' ? '' : `?projectId=${encodeURIComponent(projectId)}`;
      const response = await session.request(`/tasks/tracker-values${query}`).catch(() => null);
      if (response?.ok !== true) return;
      const parsed = taskTrackerValuesSchema.safeParse(await response.json().catch(() => null));
      if (alive.current && parsed.success) setState({ values: parsed.data, loadedFor: projectId });
    })();
    return () => {
      alive.current = false;
    };
  }, [session, projectId, after]);
  return state;
}

/** The three as query parameters — each only when set, for `?projectId=`'s reason. */
export function setTrackerParams(
  params: URLSearchParams,
  filters: Pick<TaskFilters, 'component' | 'fixVersion' | 'epic'>,
): void {
  if (filters.component !== '') params.set('component', filters.component);
  if (filters.fixVersion !== '') params.set('fixVersion', filters.fixVersion);
  if (filters.epic !== '') params.set('epic', filters.epic);
}
